// The canonical parts of a list page's query state, shared by usePageCanonical (client, the CMS pages) and the
// platform page's generateMetadata (server): a pure function, so it must stay free of hooks and React context.

export type CanonicalTranslate = (key: string, options?: Record<string, unknown>) => string

/** `<parameter>=<value>` → the filter's label; the pagination block's canonicals. */
export type Canonicals = Record<string, { name?: { defaults?: string | null } | null } | undefined>

export interface CanonicalParts {
  /** The query states with a canonical meaning, for the block's serializer. */
  canonical: Record<string, unknown>
  titleAfter: string[]
  titleBefore: string[]
}

/**
 * Only pagination (`page` above 1) and a single filter listed in the block's canonicals count; everything else in
 * the query (sorting, tracking, extra filters) is ignored, so `<link rel="canonical">`, `og:url`, the title and
 * the JSON-LD `ItemList` url all agree. `states` are the block's parsed query states (useQueryStates on the client,
 * loadQueryStates on the server), so `page` and `filter_*` are read exactly as the block reads them.
 */
export const canonicalParts = (states: Record<string, unknown>, canonicals: Canonicals, t: CanonicalTranslate): CanonicalParts => {
  const titleAfter: string[] = []
  const titleBefore: string[] = []
  const canonical: Record<string, unknown> = {}

  // the parsed page is a number on both sides; Number() keeps a stray string comparing the way `page > 1` did
  const page = Number(states.page)
  if (page > 1) {
    titleAfter.push(t('PAGINATION_PAGE_HEADER', { count: page }))
    canonical.page = page
  }

  const activeFilters = Object.keys(states).filter(key => key.startsWith('filter_') && states[key] !== null)
  if (activeFilters.length === 1) {
    const [filterKey] = activeFilters
    const values = states[filterKey]
    if (Array.isArray(values) && values.length === 1) {
      const name = canonicals[`${filterKey}=${values[0]}`]?.name?.defaults
      if (name) {
        titleBefore.push(t(name))
        canonical[filterKey] = values
      }
    }
  }

  return { canonical, titleAfter, titleBefore }
}
